import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMaterialUIController } from "context";

export interface GiftCategoryData {
    seqId?: number;
    categoryCode: string;
    categoryName: string;
    categoryDesc: string;
    sortOrder?: number;
    useYn: boolean;
}

interface Props {
    open: boolean;
    initialData?: GiftCategoryData;
    onClose: () => void;
    onConfirm: (data: GiftCategoryData) => void;
    categories: any[];
}

interface SortableItemProps {
    id: string;
    title: string;
    isCurrent: boolean;
    darkMode: boolean;
}

function SortableItem({ id, title, isCurrent, darkMode }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: isCurrent
                    ? `1px solid ${darkMode ? "#6ab7ff" : "#1A73E8"}`
                    : darkMode
                        ? "1px solid rgba(255, 255, 255, 0.2)"
                        : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: isCurrent
                    ? (darkMode ? "rgba(26, 115, 232, 0.2)" : "rgba(26, 115, 232, 0.1)")
                    : (darkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)"),
                cursor: "grab",
                "&:active": {
                    cursor: "grabbing",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                }}
            />
            <MDTypography
                variant="button"
                fontWeight={isCurrent ? "bold" : "regular"}
                sx={{
                    color: isCurrent
                        ? (darkMode ? "#90caf9" : "#1A73E8")
                        : (darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767"),
                }}
            >
                {title || "(새 카테고리)"}
            </MDTypography>
            {isCurrent && (
                <MDTypography variant="caption" color="info" sx={{ ml: "auto" }}>
                    현재 선택
                </MDTypography>
            )}
        </MDBox>
    );
}

const GifticonCategoryDialog: React.FC<Props> = ({ open, initialData, onClose, onConfirm, categories }) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [useYn, setUseYn] = useState(true);

    // For visual sorting
    const [previewList, setPreviewList] = useState<any[]>([]);

    useEffect(() => {
        if (open) {
            if (initialData) {
                // Edit Mode: Use existing list
                setName(initialData.categoryName);
                setDesc(initialData.categoryDesc);
                setUseYn(initialData.useYn);

                // Ensure current item is in the list (it should be)
                setPreviewList([...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
            } else {
                // Create Mode: Add placeholder "New Item" at the end (or appropriate pos)
                setName("");
                setDesc("");
                setUseYn(true);

                const existing = [...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                // Add a temporary object for the new item
                existing.push({
                    seqId: -1, // Temporary ID
                    categoryName: "", // Will be updated as user types? Or just "(새 항목)"
                    sortOrder: existing.length + 1
                });
                setPreviewList(existing);
            }
        }
    }, [open, initialData, categories]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setPreviewList((items) => {
                const oldIndex = items.findIndex((item) => `preview-${item.seqId}` === active.id);
                const newIndex = items.findIndex((item) => `preview-${item.seqId}` === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleConfirm = () => {
        // Determine sortOrder from previewList
        const currentSeqId = initialData ? initialData.seqId : -1;
        const index = previewList.findIndex(x => x.seqId === currentSeqId);

        // The display order is index + 1
        const calculatedSortOrder = index + 1;

        onConfirm({
            seqId: initialData?.seqId,
            categoryCode: "",
            categoryName: name,
            categoryDesc: desc,
            sortOrder: calculatedSortOrder,
            useYn: useYn
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 2000 }}>
            <DialogTitle>{initialData ? "카테고리 수정" : "카테고리 등록"}</DialogTitle>
            <DialogContent>
                <MDBox display="flex" flexDirection="column" gap={2} pt={2}>
                    <MDBox display="flex" gap={2}>
                        {/* Hidden SortOrder Input - visualised by DnD list below */}
                        <MDInput
                            label="카테고리 명"
                            value={name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setName(e.target.value);
                                // Live update label in list if it's new item
                                if (!initialData) {
                                    setPreviewList(prev => prev.map(p => p.seqId === -1 ? { ...p, categoryName: e.target.value } : p));
                                }
                            }}
                            fullWidth
                            autoFocus
                        />
                    </MDBox>
                    <MDInput
                        label="설명"
                        value={desc}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDesc(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                    />
                    <MDBox display="flex" alignItems="center">
                        <MDTypography variant="button" color="text" fontWeight="regular" mr={2}>
                            사용 여부
                        </MDTypography>
                        <Switch
                            checked={useYn}
                            onChange={(e) => setUseYn(e.target.checked)}
                        />
                    </MDBox>

                    <MDTypography variant="caption" fontWeight="bold" mt={1}>
                        순서 설정 (드래그하여 위치 변경)
                    </MDTypography>
                    <MDBox sx={{
                        maxHeight: "250px",
                        overflowY: "auto",
                        pr: 1,
                        border: "1px solid rgba(0,0,0,0.1)",
                        borderRadius: "8px",
                        padding: 1,
                        bgcolor: darkMode ? "rgba(0,0,0,0.1)" : "#fafafa"
                    }}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={previewList.map((c) => `preview-${c.seqId}`)}
                                strategy={verticalListSortingStrategy}
                            >
                                {previewList.map((c) => (
                                    <SortableItem
                                        key={c.seqId}
                                        id={`preview-${c.seqId}`}
                                        title={c.seqId === -1 ? (name || "(새 카테고리)") : c.categoryName}
                                        isCurrent={c.seqId === (initialData?.seqId ?? -1)}
                                        darkMode={darkMode}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </MDBox>
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} color="secondary">
                    취소
                </MDButton>
                <MDButton onClick={handleConfirm} color="info" disabled={!name}>
                    저장
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default GifticonCategoryDialog;
